import { useEffect } from 'react';
import { select } from 'd3';

import { getNameByLocal } from 'helpers/getNameByLocal';

const LABEL_PADDING = 10;

const dashArray = side => (side?.dash === 'dot' ? '3, 2' : '0');

// Вертикальная полоса по X (исторический вариант)
function drawXRangeFigure(group, figure, x, h) {
  const start = figure?.xrange?.start;
  const end = figure?.xrange?.end;

  if (end === undefined || end === null) {
    return;
  }

  const startX = (start !== undefined && start !== null) ? x(start) : 0;
  const endX = x(end);

  group.append('rect')
    .attr('x', startX)
    .attr('y', 0)
    .attr('width', endX - startX)
    .attr('height', h || 0)
    .attr('fill', figure?.background?.color);

  group
    .append('line')
    .attr('x1', startX)
    .attr('x2', startX)
    .attr('y1', '0')
    .attr('y2', h)
    .attr('stroke', figure?.border?.left?.color)
    .attr('stroke-width', '1')
    .attr('stroke-dasharray', dashArray(figure?.border?.left));

  group
    .append('line')
    .attr('x1', endX)
    .attr('x2', endX)
    .attr('y1', '0')
    .attr('y2', h)
    .attr('stroke', figure?.border?.right?.color)
    .attr('stroke-width', '1')
    .attr('stroke-dasharray', dashArray(figure?.border?.right));
}

// Горизонтальная полоса по Y
function drawYRangeFigure(group, figure, y, w, h, locale) {
  const { start, end } = figure.yrange;

  const clamp = value => Math.max(0, Math.min(h, value));

  // y инвертирована: y(end) сверху, y(start) снизу. Клампим к видимой области [0, h],
  // т.к. диапазон полосы может выходить за домен оси (например, начинаться с 0)
  const rawTopY = y(end);
  const rawBottomY = y(start);
  const topY = clamp(rawTopY);
  const bottomY = clamp(rawBottomY);

  // Полоса целиком вне области отрисовки — не рисуем
  if (topY === bottomY) {
    return;
  }

  group.append('rect')
    .attr('x', 0)
    .attr('y', Math.min(topY, bottomY))
    .attr('width', w || 0)
    .attr('height', Math.abs(bottomY - topY))
    .attr('fill', figure?.background?.color);

  // Границы рисуем только если они попадают в видимую область
  if (figure?.border?.top && rawTopY >= 0 && rawTopY <= h) {
    group
      .append('line')
      .attr('x1', 0)
      .attr('x2', w)
      .attr('y1', rawTopY)
      .attr('y2', rawTopY)
      .attr('stroke', figure.border.top.color)
      .attr('stroke-width', '1')
      .attr('stroke-dasharray', dashArray(figure.border.top));
  }

  if (figure?.border?.bottom && rawBottomY >= 0 && rawBottomY <= h) {
    group
      .append('line')
      .attr('x1', 0)
      .attr('x2', w)
      .attr('y1', rawBottomY)
      .attr('y2', rawBottomY)
      .attr('stroke', figure.border.bottom.color)
      .attr('stroke-width', '1')
      .attr('stroke-dasharray', dashArray(figure.border.bottom));
  }

  if (figure?.label?.text) {
    const align = figure.label.align || 'right';
    const verticalAlign = figure.label.verticalAlign || 'center';

    let labelX = w - LABEL_PADDING;
    let textAnchor = 'end';
    if (align === 'left') {
      labelX = LABEL_PADDING;
      textAnchor = 'start';
    } else if (align === 'center') {
      labelX = w / 2;
      textAnchor = 'middle';
    }

    let labelY = (topY + bottomY) / 2;
    let baseline = 'central';
    if (verticalAlign === 'top') {
      labelY = topY + LABEL_PADDING;
      baseline = 'hanging';
    } else if (verticalAlign === 'bottom') {
      labelY = bottomY - LABEL_PADDING;
      baseline = 'alphabetic';
    }

    group
      .append('text')
      .attr('class', 'label')
      .attr('x', labelX)
      .attr('y', labelY)
      .attr('font-size', '12px')
      .attr('fill', '#9CA8B4')
      .attr('text-anchor', textAnchor)
      .attr('alignment-baseline', baseline)
      .text(getNameByLocal(figure.label, locale, 'text'));
  }
}

function useOverlay(overlayRef, options) {
  const {
    chartParams: {
      x, y, w, h
    },
    overlayFigures,
    locale,
  } = options;

  useEffect(() => {
    const group = overlayRef.current
      ? select(overlayRef.current)
      : null;

    if (!group) {
      return;
    }

    group.selectAll('*').remove();

    if (!overlayFigures?.length) {
      return;
    }

    // Ширина области отрисовки = диапазон X-шкалы (а не полная ширина SVG),
    // иначе горизонтальные полосы и их подписи вылезают за правый край графика
    const plotWidth = (typeof x?.range === 'function' && x.range()[1]) || w;

    overlayFigures.forEach((figure) => {
      if (figure?.yrange) {
        drawYRangeFigure(group, figure, y, plotWidth, h, locale);
      } else if (figure?.xrange) {
        drawXRangeFigure(group, figure, x, h);
      }
    });
  }, [overlayRef, overlayFigures, x, y, w, h, locale]);
}

export default useOverlay;
